import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useLanguage } from '../../context/LanguageContext';
import AdminLayout from '../../components/layout/AdminLayout';
import { Card, CardContent } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from '../../components/ui/alert-dialog';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../../components/ui/dialog';
import { Label } from '../../components/ui/label';
import { toast } from 'sonner';
import { Plus, Search, Edit, Trash2, ClipboardList, Loader2, ChevronRight, Copy, Sparkles, Heart, Waves, Flame, HeartPulse, Wind, Users } from 'lucide-react';
import ClonePlanDialog from '../../components/admin/ClonePlanDialog';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const PlansPage = () => {
  const { t } = useLanguage();
  const navigate = useNavigate();
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [selectedPlan, setSelectedPlan] = useState(null);
  const [duplicateDialogOpen, setDuplicateDialogOpen] = useState(false);
  const [duplicateName, setDuplicateName] = useState('');
  const [duplicating, setDuplicating] = useState(false);
  const [creatingSenior, setCreatingSenior] = useState(false);
  const [creatingSEA, setCreatingSEA] = useState(false);
  const [creatingBASI, setCreatingBASI] = useState(false);
  const [creatingANA, setCreatingANA] = useState(false);
  const [creatingTEP, setCreatingTEP] = useState(false);
  const [repairingAdolescente, setRepairingAdolescente] = useState(false);
  const [cloneTarget, setCloneTarget] = useState(null);

  useEffect(() => {
    fetchPlans();
  }, []);

  const fetchPlans = async () => {
    try {
      const response = await axios.get(`${API}/plans`);
      setPlans(response.data);
    } catch (error) {
      console.error('Failed to fetch plans:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    try {
      await axios.delete(`${API}/plans/${selectedPlan.id}`);
      toast.success(t('planDeleted'));
      setDeleteDialogOpen(false);
      fetchPlans();
    } catch (error) {
      toast.error(t('error'));
    }
  };

  const handleDuplicate = async () => {
    if (!duplicateName.trim()) {
      toast.error('Introduce un nombre para el nuevo plan');
      return;
    }
    
    try {
      setDuplicating(true);
      const response = await axios.post(`${API}/plans/${selectedPlan.id}/duplicate?new_name=${encodeURIComponent(duplicateName)}`);
      toast.success(response.data.message);
      setDuplicateDialogOpen(false);
      setDuplicateName('');
      fetchPlans();
    } catch (error) {
      toast.error('Error al duplicar el plan');
    } finally {
      setDuplicating(false);
    }
  };

  const openDuplicateDialog = (plan) => {
    setSelectedPlan(plan);
    setDuplicateName(`${plan.name} (Copia)`);
    setDuplicateDialogOpen(true);
  };

  // Crear Plan Senior
  const createPlanSenior = async () => {
    try {
      setCreatingSenior(true);
      const response = await axios.post(`${API}/admin/create-senior-plan`);
      toast.success(response.data.message || '✅ Plan Senior creado');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear Plan Senior');
    } finally {
      setCreatingSenior(false);
    }
  };

  // Crear Plan SEA
  const createPlanSEA = async () => {
    try {
      setCreatingSEA(true);
      const response = await axios.post(`${API}/admin/create-plan-sea`);
      toast.success(response.data.message || '✅ Plan SEA creado');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear Plan SEA');
    } finally {
      setCreatingSEA(false);
    }
  };

  // Crear Plan BASI - Proceso Atlético
  const createPlanBASI = async () => {
    try {
      setCreatingBASI(true);
      const response = await axios.post(`${API}/admin/create-plan-basi`);
      toast.success(response.data.message || '🔥 Plan BASI creado');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear Plan BASI');
    } finally {
      setCreatingBASI(false);
    }
  };

  // Crear Plan ANA - Endometriosis
  const createPlanANA = async () => {
    try {
      setCreatingANA(true);
      const response = await axios.post(`${API}/admin/create-plan-ana`);
      toast.success(response.data.message || '🩺 Plan Ana creado');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear Plan Ana');
    } finally {
      setCreatingANA(false);
    }
  };

  // Crear Plan TEP - Tromboembolia Pulmonar
  const createPlanTEP = async () => {
    try {
      setCreatingTEP(true);
      const response = await axios.post(`${API}/admin/create-plan-tep`);
      toast.success(response.data.message || '🫁 Plan TEP creado');
      fetchPlans();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Error al crear Plan TEP');
    } finally {
      setCreatingTEP(false);
    }
  };

  // Verificar si los planes ya existen
  const hasSeniorPlan = plans.some(p => p.name.toLowerCase().includes('senior'));
  const hasSEAPlan = plans.some(p => p.name.toLowerCase().includes('sea'));
  const hasBASIPlan = plans.some(p => p.name.toLowerCase().includes('basi'));
  const hasANAPlan = plans.some(p => p.name.toLowerCase().includes('ana') || p.plan_code === 'GF-ENDO-ANA-001');
  const hasTEPPlan = plans.some(p => p.name.toLowerCase().includes('tep') || p.plan_code === 'GF-TEP-001');

  const filteredPlans = plans.filter(plan =>
    plan.name.toLowerCase().includes(search.toLowerCase()) ||
    plan.objective.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <AdminLayout>
      <div className="space-y-6 animate-fade-in" data-testid="plans-page">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl font-bold text-foreground">{t('plans')}</h1>
            <p className="text-muted-foreground">{filteredPlans.length} {t('plans').toLowerCase()}</p>
          </div>
          <div className="flex flex-wrap gap-2">
            {/* Botón crear Plan Senior */}
            <Button 
              variant="outline"
              className={`border-amber-500/50 text-amber-400 hover:bg-amber-500/10 ${hasSeniorPlan ? 'opacity-50' : ''}`}
              onClick={createPlanSenior}
              disabled={creatingSenior}
              title={hasSeniorPlan ? 'Ya existe - Click para actualizar' : 'Crear Plan Senior'}
            >
              {creatingSenior ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Heart className="h-4 w-4 mr-2" />}
              {hasSeniorPlan ? 'Actualizar Senior' : 'Crear Senior'}
            </Button>
            
            {/* Botón crear Plan SEA */}
            <Button 
              variant="outline"
              className={`border-cyan-500/50 text-cyan-400 hover:bg-cyan-500/10 ${hasSEAPlan ? 'opacity-50' : ''}`}
              onClick={createPlanSEA}
              disabled={creatingSEA}
              title={hasSEAPlan ? 'Ya existe - Click para actualizar' : 'Crear Plan SEA'}
            >
              {creatingSEA ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Waves className="h-4 w-4 mr-2" />}
              {hasSEAPlan ? 'Actualizar SEA' : 'Crear SEA'}
            </Button>

            {/* Botón crear Plan BASI */}
            <Button 
              variant="outline"
              className={`border-orange-500/50 text-orange-400 hover:bg-orange-500/10 ${hasBASIPlan ? 'opacity-50' : ''}`}
              onClick={createPlanBASI}
              disabled={creatingBASI}
              title={hasBASIPlan ? 'Ya existe - Click para actualizar' : 'Crear Plan BASI'}
              data-testid="create-basi-button"
            >
              {creatingBASI ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Flame className="h-4 w-4 mr-2" />}
              {hasBASIPlan ? 'Actualizar BASI' : 'Crear BASI'}
            </Button>

            {/* Botón crear Plan ANA - Endometriosis */}
            <Button 
              variant="outline"
              className={`border-pink-500/50 text-pink-400 hover:bg-pink-500/10 ${hasANAPlan ? 'opacity-50' : ''}`}
              onClick={createPlanANA}
              disabled={creatingANA}
              title={hasANAPlan ? 'Ya existe - Click para actualizar' : 'Crear Plan Ana (Endometriosis)'}
              data-testid="create-ana-button"
            >
              {creatingANA ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <HeartPulse className="h-4 w-4 mr-2" />}
              {hasANAPlan ? 'Actualizar Ana' : 'Crear Ana'}
            </Button>

            {/* Botón crear Plan TEP - Tromboembolia Pulmonar */}
            <Button 
              variant="outline"
              className={`border-sky-500/50 text-sky-400 hover:bg-sky-500/10 ${hasTEPPlan ? 'opacity-50' : ''}`}
              onClick={createPlanTEP}
              disabled={creatingTEP}
              title={hasTEPPlan ? 'Ya existe - Click para actualizar' : 'Crear Plan TEP (Tromboembolia Pulmonar)'}
              data-testid="create-tep-button"
            >
              {creatingTEP ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Wind className="h-4 w-4 mr-2" />}
              {hasTEPPlan ? 'Actualizar TEP' : 'Crear TEP'}
            </Button>
            
            {/* Botón nuevo plan */}
            <Button 
              className="bg-primary text-primary-foreground hover:bg-primary/90"
              onClick={() => navigate('/admin/plans/new')}
              data-testid="new-plan-button"
            >
              <Plus className="h-4 w-4 mr-2" />
              {t('newPlan')}
            </Button>
            
            {/* Botón dejar solo Meso 1 */}
            <Button 
              variant="outline"
              className="border-amber-500/50 text-amber-400 hover:bg-amber-500/10"
              onClick={async () => {
                const planId = window.prompt('ID del plan (lo ves en la URL):');
                if (!planId) return;
                
                if (!window.confirm(`¿Dejar SOLO las primeras 5 semanas (Mesociclo 1)?\n\nSe borrarán las semanas 6 en adelante.\n\nID: ${planId}`)) return;
                if (!window.confirm('SEGUNDA CONFIRMACIÓN: ¿Seguro? Esto borra semanas 6-10.')) return;
                
                try {
                  const response = await axios.post(`${API}/admin/plans/${planId}/keep-only-meso1`);
                  if (response.data.fixed) {
                    toast.success(response.data.message);
                    fetchPlans();
                  } else {
                    toast.error(response.data.error || 'Error');
                  }
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Error');
                }
              }}
              title="Dejar solo las primeras 5 semanas como Mesociclo 1"
            >
              <Sparkles className="h-4 w-4 mr-2" />
              Solo Meso 1
            </Button>

            {/* Botón añadir Mesociclo 2 */}
            <Button 
              variant="outline"
              className="border-green-500/50 text-green-400 hover:bg-green-500/10"
              onClick={async () => {
                const planId = window.prompt('Introduce el ID del plan (lo ves en la URL cuando editas el plan):');
                if (!planId) return;
                
                const confirmMsg = `¿CONFIRMAS añadir Mesociclo 2 al plan?\n\nEsto duplicará la última semana (semana 5 o 6) para crear semanas hasta la 10.\n\nID del plan: ${planId}`;
                if (!window.confirm(confirmMsg)) return;
                
                // DOBLE CONFIRMACIÓN
                if (!window.confirm('SEGUNDA CONFIRMACIÓN: ¿Estás seguro? Esta acción modificará el plan.')) return;
                
                setRepairingAdolescente(true);
                try {
                  const response = await axios.post(`${API}/admin/plans/${planId}/add-mesocycle-2`);
                  if (response.data.fixed) {
                    toast.success(response.data.message);
                    fetchPlans();
                  } else {
                    toast.error(response.data.error || 'No se pudo añadir');
                  }
                } catch (error) {
                  toast.error(error.response?.data?.detail || 'Error');
                } finally {
                  setRepairingAdolescente(false);
                }
              }}
              disabled={repairingAdolescente}
              title="Añadir Mesociclo 2 a un plan que tenga 5 semanas"
              data-testid="add-meso2-button"
            >
              {repairingAdolescente ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Plus className="h-4 w-4 mr-2" />}
              + Añadir Meso 2
            </Button>
          </div>
        </div>

        {/* Search */}
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input
            placeholder={t('search')}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            className="pl-10 bg-card border-border"
            data-testid="plan-search-input"
          />
        </div>

        {/* Plans Grid */}
        {loading ? (
          <div className="flex items-center justify-center py-12">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
          </div>
        ) : filteredPlans.length === 0 ? (
          <Card className="bg-card border-border">
            <CardContent className="flex flex-col items-center justify-center py-12">
              <ClipboardList className="h-12 w-12 text-muted-foreground mb-4" />
              <p className="text-muted-foreground">{t('noPlans')}</p>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 stagger-children">
            {filteredPlans.map((plan) => (
              <Card 
                key={plan.id} 
                className="bg-card border-border card-hover cursor-pointer"
                onClick={() => navigate(`/admin/plans/${plan.id}`)}
                data-testid={`plan-card-${plan.id}`}
              >
                <CardContent className="p-6">
                  <div className="flex items-start justify-between">
                    <div className="flex-1">
                      <div className="flex items-center gap-3 mb-2">
                        <div className="p-2 rounded-sm bg-emerald-500/10">
                          <ClipboardList className="h-5 w-5 text-emerald-500" strokeWidth={1.5} />
                        </div>
                        <h3 className="font-semibold text-foreground">{plan.name}</h3>
                      </div>
                      <p className="text-sm text-primary mb-2">{plan.objective}</p>
                      {plan.description && (
                        <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                          {plan.description}
                        </p>
                      )}
                      <div className="flex items-center gap-4 text-xs text-muted-foreground">
                        <span>{plan.weeks?.length || 0} {t('weeks').toLowerCase()}</span>
                        <span>
                          {plan.weeks?.reduce((acc, w) => acc + (w.sessions?.length || 0), 0) || 0} {t('sessions').toLowerCase()}
                        </span>
                      </div>
                    </div>
                    <div className="flex flex-col gap-1" onClick={(e) => e.stopPropagation()}>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); navigate(`/admin/plans/${plan.id}`); }}
                        data-testid={`edit-plan-${plan.id}`}
                        title="Editar plan"
                      >
                        <Edit className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); openDuplicateDialog(plan); }}
                        className="hover:text-cyan-400"
                        data-testid={`duplicate-plan-${plan.id}`}
                        title="Duplicar plan"
                      >
                        <Copy className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); setCloneTarget(plan); }}
                        className="hover:text-emerald-400"
                        data-testid={`clone-assign-plan-${plan.id}`}
                        title="Clonar y asignar a cliente"
                      >
                        <Users className="h-4 w-4" />
                      </Button>
                      <Button
                        variant="ghost"
                        size="icon"
                        onClick={(e) => { e.stopPropagation(); setSelectedPlan(plan); setDeleteDialogOpen(true); }}
                        className="hover:text-destructive"
                        data-testid={`delete-plan-${plan.id}`}
                        title="Eliminar plan"
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    </div>
                  </div>
                </CardContent>
              </Card>
            ))}
          </div>
        )}

        {/* Delete Confirmation */}
        <AlertDialog open={deleteDialogOpen} onOpenChange={setDeleteDialogOpen}>
          <AlertDialogContent className="bg-card border-border">
            <AlertDialogHeader>
              <AlertDialogTitle>{t('deletePlan')}</AlertDialogTitle>
              <AlertDialogDescription>
                {selectedPlan?.name}
              </AlertDialogDescription>
            </AlertDialogHeader>
            <AlertDialogFooter>
              <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
              <AlertDialogAction 
                onClick={handleDelete}
                className="bg-destructive text-destructive-foreground"
                data-testid="confirm-delete-plan"
              >
                {t('delete')}
              </AlertDialogAction>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialog>

        {/* Duplicate Plan Dialog */}
        <Dialog open={duplicateDialogOpen} onOpenChange={setDuplicateDialogOpen}>
          <DialogContent className="bg-card border-border">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <Copy className="h-5 w-5 text-cyan-400" />
                Duplicar Plan
              </DialogTitle>
            </DialogHeader>
            <div className="space-y-4 py-4">
              <p className="text-sm text-muted-foreground">
                Se creará una copia exacta de <span className="text-foreground font-semibold">"{selectedPlan?.name}"</span> con todas sus semanas, sesiones y ejercicios.
              </p>
              <div className="space-y-2">
                <Label htmlFor="new-plan-name">Nombre del nuevo plan:</Label>
                <Input
                  id="new-plan-name"
                  value={duplicateName}
                  onChange={(e) => setDuplicateName(e.target.value)}
                  placeholder="Nombre del nuevo plan"
                  className="bg-background border-border"
                />
              </div>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDuplicateDialogOpen(false)}>
                Cancelar
              </Button>
              <Button 
                onClick={handleDuplicate}
                disabled={duplicating}
                className="bg-cyan-600 hover:bg-cyan-700"
              >
                {duplicating ? (
                  <>
                    <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    Duplicando...
                  </>
                ) : (
                  <>
                    <Copy className="h-4 w-4 mr-2" />
                    Duplicar Plan
                  </>
                )}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        <ClonePlanDialog
          open={!!cloneTarget}
          onClose={() => setCloneTarget(null)}
          plan={cloneTarget}
          role="admin"
          onCloned={() => fetchPlans()}
        />
      </div>
    </AdminLayout>
  );
};

export default PlansPage;
